import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { publicInvitationsApi, guestsApi } from '../../_lib/api-client';
import { InvitationPage } from '../../_graphql/generated/types';
import { InvitationDesign } from '../../_types/invitation-design-v2';
import InvitationPDFWrapper from '../../_components/InvitationPDFWrapper';
import PDFDownloadModal, { PageSize, PAGE_SIZES } from '../../_components/PDFDownloadModal';
import { generateInvitationPDF } from '../../_utils/pdfGenerator';
import { useTranslation } from 'react-i18next';

const EventHomepage: React.FC = () => {
  const { eventCode, token, eventName } = useParams<{ eventCode: string; token: string; eventName: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [invitationPage, setInvitationPage] = useState<InvitationPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showChangeEmailModal, setShowChangeEmailModal] = useState(false);
  const [keepUpdated, setKeepUpdated] = useState(true);
  const [emailPDFForm, setEmailPDFForm] = useState({ email: '', keepUpdated: true });
  const [showPDFModal, setShowPDFModal] = useState(false);
  const [showAdvancedPDFOptions, setShowAdvancedPDFOptions] = useState(false);
  const [pdfPageSize, setPdfPageSize] = useState<PageSize | null>(null);
  const [pdfRemoveBackground, setPdfRemoveBackground] = useState(false);
  const [pdfRemoveBorder, setPdfRemoveBorder] = useState(false);
  const [design, setDesign] = useState<InvitationDesign | null>(null);

  useEffect(() => {
    const fetchInvitation = async () => {
      if (!token || !eventCode) {
        setError('Invalid event link');
        setLoading(false);
        return;
      }

      try {
        const invitationData = await publicInvitationsApi.getByToken(token, eventCode);
        
        if (!invitationData) {
          setError('Event not found');
          setLoading(false);
          return;
        }

        setInvitationPage(invitationData);
        
        // Debug log (will be removed later)
        console.log('EventHomepage: Additional details loaded:', {
          eventId: invitationData.event?.id,
          eventCode: invitationData.event?.code,
          count: invitationData.event?.additionalDetails?.length || 0,
          details: invitationData.event?.additionalDetails
        });
        
        // Parse the design
        try {
          if (invitationData.design) {
            if (typeof invitationData.design === 'string') {
              setDesign(JSON.parse(invitationData.design));
            } else {
              setDesign(invitationData.design as InvitationDesign);
            }
          }
        } catch (err) {
          console.error('Failed to parse design:', err);
        }
      } catch (err) {
        setError('Failed to load event information');
        console.error('Error fetching event:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchInvitation();
  }, [token, eventCode]);

  // Update document title when invitation page is loaded
  useEffect(() => {
    if (invitationPage?.event?.eventName) {
      document.title = `${invitationPage.event.eventName} - invitewithlove.com`;
    }
    
    // Reset to default title on unmount
    return () => {
      document.title = 'invitewithlove.com';
    };
  }, [invitationPage?.event?.eventName]);


  const handleChangeRSVP = () => {
    // Navigate back to the invitation page with state to indicate RSVP change
    navigate(`/invitations/${eventCode}/${token}`, { state: { changeRSVP: true } });
  };

  const handleEmailSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!invitationPage) return;
    
    const formData = new FormData(e.currentTarget);
    const email = formData.get('email') as string;
    
    if (!email || !email.includes('@')) {
      setEmailError(t('email.errors.invalid'));
      return;
    }
    
    setEmailLoading(true);
    setEmailError(null);
    
    try {
      // Update email for all primary guests (main guest only, not plus ones)
      const primaryGuests = invitationPage.invitation.guests.filter(guest => !guest.primaryGuestId);
      
      // Update the first primary guest with the email
      if (primaryGuests.length > 0) {
        await guestsApi.update({
          id: primaryGuests[0].id,
          email: email
        });
      }
      
      setEmailSuccess(true);
      // Reset form
      (e.target as HTMLFormElement).reset();
      
      // Close the modal after successful save
      setShowChangeEmailModal(false);
      
      // Hide success message after 3 seconds
      setTimeout(() => setEmailSuccess(false), 3000);
    } catch (error) {
      console.error('Error updating guest email:', error);
      setEmailError(t('email.errors.submitFailed'));
    } finally {
      setEmailLoading(false);
    }
  };

  const handleEmailPDF = async () => {
    // TODO: Implement actual email sending
    console.log('Email PDF to:', invitationPage?.invitation.guests.find(g => !g.primaryGuestId)?.email);
    // Show success message
    alert(t('pdf.emailSent'));
  };

  const handleEmailPDFSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!invitationPage) return;
    
    const formData = new FormData(e.currentTarget);
    const email = formData.get('email') as string;
    const keepUpdated = formData.get('keepUpdated') === 'on';
    
    if (!email || !email.includes('@')) {
      setEmailError(t('email.errors.invalid'));
      return;
    }
    
    setEmailLoading(true);
    setEmailError(null);
    
    try {
      // Update email for the primary guest
      const primaryGuest = invitationPage.invitation.guests.find(guest => !guest.primaryGuestId);
      
      if (primaryGuest) {
        await guestsApi.update({
          id: primaryGuest.id,
          email: email
        });
      }
      
      // Send the PDF via email
      await handleEmailPDF();
      
      // Close modal
      setShowEmailModal(false);
      
      // Reset form
      setEmailPDFForm({ email: '', keepUpdated: true });
    } catch (error) {
      console.error('Error updating guest email:', error);
      setEmailError(t('email.errors.submitFailed'));
    } finally {
      setEmailLoading(false);
    }
  };

  const handlePDFDownload = async (pageSize: PageSize, quality: 'standard' | 'high', includeBleed: boolean, removeBackground: boolean, removeBorder: boolean) => {
    if (!invitationPage) return;
    
    // Store the selected page size and options for rendering the PDF version
    setPdfPageSize(pageSize);
    setPdfRemoveBackground(removeBackground);
    setPdfRemoveBorder(removeBorder);
    
    // Wait for the PDF version to render
    await new Promise(resolve => setTimeout(resolve, 100));
    
    const fileName = `${invitationPage.event.eventName.replace(/[^a-z0-9]/gi, '_')}_invitation.pdf`;
    
    await generateInvitationPDF({
      elementId: 'pdf-invitation-wrapper',
      fileName,
      pageSize,
      quality,
      includeBleed,
      removeBackground,
      removeBorder,
    });
    
    // Clear the PDF page size and options after generation
    setPdfPageSize(null);
    setPdfRemoveBackground(false);
    setPdfRemoveBorder(false);
  };

  const handleQuickPDFDownload = async () => {
    // Quick download with default A5 150 DPI settings
    await handlePDFDownload(PAGE_SIZES[0], 'standard', false, false, false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading event details...</p>
        </div>
      </div>
    );
  }

  if (error || !invitationPage) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <svg className="w-12 h-12 text-red-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h2 className="mt-4 text-xl font-semibold text-gray-900">Event Not Found</h2>
          <p className="mt-2 text-gray-600">{error || 'This event link may be invalid or expired.'}</p>
        </div>
      </div>
    );
  }

  const eventDate = new Date(invitationPage.event.eventDateTime);
  const daysUntil = Math.ceil((eventDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section with Couple Names */}
      <div className="relative h-96 bg-gradient-to-b from-gray-100 to-gray-50 overflow-hidden">
        <div className="absolute inset-0 bg-white/30"></div>
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4">
          <h1 className="text-5xl md:text-6xl font-serif text-gray-800 mb-4">
            {invitationPage.event.eventName}
          </h1>
          <p className="text-xl text-gray-600">
            {eventDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
          <div className="mt-8 px-6 py-3 bg-white/80 backdrop-blur rounded-full shadow-md">
            <p className="text-2xl font-light text-gray-700">
              {daysUntil > 0 ? `${daysUntil} days to go!` : 'Today is the day!'}
            </p>
          </div>
        </div>
      </div>

      {/* Welcome Message */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">Thank you for your RSVP!</h2>
          <p className="text-gray-600 leading-relaxed mb-6">
            We're so excited to celebrate with you. Here's everything you need to know about our special day.
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleChangeRSVP}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Change RSVP
            </button>
            
            {/* PDF Download Section */}
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={handleQuickPDFDownload}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {t('pdf.downloadButton')}
              </button>
              {/* Email PDF button - hidden for now
              <button
                onClick={() => {
                  const primaryGuest = invitationPage?.invitation.guests.find(g => !g.primaryGuestId);
                  if (primaryGuest?.email) {
                    // If we have email, just email the PDF
                    handleEmailPDF();
                  } else {
                    // Show modal to collect email
                    setShowEmailModal(true);
                  }
                }}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                {t('pdf.emailButton')}
              </button>
              */}
              {/* Hidden for now
              <button
                onClick={() => setShowPDFModal(true)}
                className="text-sm text-gray-600 hover:text-gray-800 underline"
              >
                More options
              </button>
              */}
            </div>
          </div>
        </div>

        {/* Quick Info Cards */}
        {(() => {
          const infoCardsVisible = invitationPage.event.additionalDetails?.find(d => d.key === 'info_cards_visible')?.value !== 'false';
          const infoCardsColumns = invitationPage.event.additionalDetails?.find(d => d.key === 'info_cards_columns')?.value || '3';
          
          if (!infoCardsVisible) return null;
          
          const visibleCards = invitationPage.event.additionalDetails?.filter(d => 
            d.displayOnPage && 
            !d.key.startsWith('_') &&
            !d.key.startsWith('FAQ_') &&
            !d.key.startsWith('Contact_') &&
            !d.key.startsWith('Hotel_') &&
            !d.key.includes('_visible') &&
            !d.key.includes('_title') &&
            !d.key.includes('_columns') &&
            d.key !== 'Accommodation_Notes'
          ) || [];
          
          if (visibleCards.length > 0) {
            return (
              <div className={`grid md:grid-cols-${infoCardsColumns} gap-6 mb-12`}>
                {visibleCards.map((detail, index) => {
                // Determine icon and color based on key
                let iconColor = 'indigo';
                let bgColor = 'bg-indigo-100';
                let textColor = 'text-indigo-600';
                let icon = (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                );
                
                if (detail.key.toLowerCase().includes('schedule') || detail.key.toLowerCase().includes('time')) {
                  bgColor = 'bg-indigo-100';
                  textColor = 'text-indigo-600';
                  icon = (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  );
                } else if (detail.key.toLowerCase().includes('venue') || detail.key.toLowerCase().includes('location')) {
                  bgColor = 'bg-emerald-100';
                  textColor = 'text-emerald-600';
                  icon = (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  );
                } else if (detail.key.toLowerCase().includes('gift') || detail.key.toLowerCase().includes('registry')) {
                  bgColor = 'bg-rose-100';
                  textColor = 'text-rose-600';
                  icon = (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                    </svg>
                  );
                } else if (detail.key.toLowerCase().includes('dress')) {
                  bgColor = 'bg-purple-100';
                  textColor = 'text-purple-600';
                  icon = (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                  );
                } else if (detail.key.toLowerCase().includes('transport') || detail.key.toLowerCase().includes('parking')) {
                  bgColor = 'bg-blue-100';
                  textColor = 'text-blue-600';
                  icon = (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                    </svg>
                  );
                } else if (detail.key.toLowerCase().includes('accommodation') || detail.key.toLowerCase().includes('hotel')) {
                  bgColor = 'bg-amber-100';
                  textColor = 'text-amber-600';
                  icon = (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                  );
                }
                
                return (
                  <div key={detail.key} className="bg-white rounded-lg shadow-md p-6">
                    <div className={`w-12 h-12 ${bgColor} rounded-full flex items-center justify-center mb-4`}>
                      <div className={textColor}>
                        {icon}
                      </div>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">{detail.key}</h3>
                    <div className="text-gray-600 text-sm whitespace-pre-wrap">
                      {detail.value.split('\n').map((line, i) => (
                        <React.Fragment key={i}>
                          {i > 0 && <br />}
                          {/* Simple markdown support for bold and italic */}
                          {line.split(/(\*\*.*?\*\*|\*.*?\*)/).map((part, j) => {
                            if (part.startsWith('**') && part.endsWith('**')) {
                              return <strong key={j}>{part.slice(2, -2)}</strong>;
                            } else if (part.startsWith('*') && part.endsWith('*') && !part.startsWith('**')) {
                              return <em key={j}>{part.slice(1, -1)}</em>;
                            }
                            return part;
                          })}
                        </React.Fragment>
                      ))}
                    </div>
                    
                    {/* Add Google Maps link for venue */}
                    {detail.key.toLowerCase().includes('venue') && detail.value.includes('\n') && (
                      <p className="mt-3">
                        <a
                          href={`https://maps.google.com/maps?q=${encodeURIComponent(detail.value)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-indigo-600 hover:underline text-sm"
                        >
                          Show on a map
                        </a>
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          );
          } else {
            // Show default placeholder cards if no info cards configured
            return (
              <div className="grid md:grid-cols-3 gap-6 mb-12">
            {/* Fallback to hardcoded cards if no additional details */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Schedule</h3>
              <p className="text-gray-600 text-sm">
                Please check with the event organizer for schedule details.
              </p>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Venue</h3>
              <p className="text-gray-600 text-sm">
                Please check with the event organizer for venue details.
              </p>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="w-12 h-12 bg-rose-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Gift Registry</h3>
              <p className="text-gray-600 text-sm">
                Please check with the event organizer for gift registry details.
              </p>
            </div>
          </div>
          );
          }
        })()}


        {/* FAQs */}
        {(() => {
          const faqVisibleSetting = invitationPage.event.additionalDetails?.find(d => d.key === 'faq_visible')?.value;
          const faqVisible = faqVisibleSetting !== 'false'; // Show by default unless explicitly set to false
          const faqTitle = invitationPage.event.additionalDetails?.find(d => d.key === 'faq_title')?.value || 'Frequently Asked Questions';
          
          // Collect all FAQ Q&A pairs
          const faqs = [];
          for (let i = 1; i <= 5; i++) {
            const question = invitationPage.event.additionalDetails?.find(d => d.key === `FAQ_Q${i}`)?.value;
            const answer = invitationPage.event.additionalDetails?.find(d => d.key === `FAQ_A${i}`)?.value;
            if (question && answer) {
              faqs.push({ question, answer });
            }
          }
          
          if (faqVisible && faqs.length > 0) {
            return (
              <div className="bg-white rounded-lg shadow-md p-8 mt-8">
                <h3 className="text-xl font-semibold text-gray-800 mb-6">{faqTitle}</h3>
                <div className="space-y-4">
                  {faqs.map((faq, index) => (
                    <div key={index}>
                      <h4 className="font-medium text-gray-800 mb-1">{faq.question}</h4>
                      <p className="text-gray-600 text-sm whitespace-pre-wrap">{faq.answer}</p>
                    </div>
                  ))}
                </div>
              </div>
            );
          }
          return null;
        })()}

        {/* Contact Section */}
        {(() => {
          const contactVisibleSetting = invitationPage.event.additionalDetails?.find(d => d.key === 'contact_visible')?.value;
          const contactVisible = contactVisibleSetting !== 'false'; // Show by default unless explicitly set to false
          const contactTitle = invitationPage.event.additionalDetails?.find(d => d.key === 'contact_title')?.value || 'Questions?';
          const contactEmail = invitationPage.event.additionalDetails?.find(d => d.key === 'Contact_Email')?.value;
          const contactPhone = invitationPage.event.additionalDetails?.find(d => d.key === 'Contact_Phone')?.value;
          const contactMessage = invitationPage.event.additionalDetails?.find(d => d.key === 'Contact_Message')?.value;
          
          if (contactVisible && (contactEmail || contactPhone || contactMessage)) {
            return (
              <div className="bg-white rounded-lg shadow-md p-8 mt-8">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">{contactTitle}</h3>
                {contactMessage && (
                  <p className="text-gray-600 mb-4">{contactMessage}</p>
                )}
                <div className="flex flex-col sm:flex-row gap-4">
                  {contactEmail && (
                    <a
                      href={`mailto:${contactEmail}`}
                      className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      {contactEmail}
                    </a>
                  )}
                  {contactPhone && (
                    <a
                      href={`tel:${contactPhone}`}
                      className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      {contactPhone}
                    </a>
                  )}
                </div>
              </div>
            );
          }
          return null;
        })()}

        {/* Accommodation Section */}
        {(() => {
          const accommodationVisibleSetting = invitationPage.event.additionalDetails?.find(d => d.key === 'accommodation_visible')?.value;
          const accommodationVisible = accommodationVisibleSetting === 'true'; // Hidden by default, must be explicitly enabled
          const accommodationTitle = invitationPage.event.additionalDetails?.find(d => d.key === 'accommodation_title')?.value || 'Where to Stay';
          const hotel1Name = invitationPage.event.additionalDetails?.find(d => d.key === 'Hotel_1_Name')?.value;
          const hotel1Details = invitationPage.event.additionalDetails?.find(d => d.key === 'Hotel_1_Details')?.value;
          const hotel2Name = invitationPage.event.additionalDetails?.find(d => d.key === 'Hotel_2_Name')?.value;
          const hotel2Details = invitationPage.event.additionalDetails?.find(d => d.key === 'Hotel_2_Details')?.value;
          const accommodationNotes = invitationPage.event.additionalDetails?.find(d => d.key === 'Accommodation_Notes')?.value;
          
          if (accommodationVisible && (hotel1Name || hotel2Name || accommodationNotes)) {
            return (
              <div className="bg-white rounded-lg shadow-md p-8 mt-8">
                <h3 className="text-xl font-semibold text-gray-800 mb-6">{accommodationTitle}</h3>
                <div className="space-y-6">
                  {hotel1Name && (
                    <div>
                      <h4 className="font-medium text-gray-800 mb-2">{hotel1Name}</h4>
                      {hotel1Details && (
                        <p className="text-gray-600 text-sm whitespace-pre-wrap">{hotel1Details}</p>
                      )}
                    </div>
                  )}
                  {hotel2Name && (
                    <div>
                      <h4 className="font-medium text-gray-800 mb-2">{hotel2Name}</h4>
                      {hotel2Details && (
                        <p className="text-gray-600 text-sm whitespace-pre-wrap">{hotel2Details}</p>
                      )}
                    </div>
                  )}
                  {accommodationNotes && (
                    <p className="text-gray-600 text-sm whitespace-pre-wrap">{accommodationNotes}</p>
                  )}
                </div>
              </div>
            );
          }
          return null;
        })()}

        {/* Email Subscription Panel - Only show if configured and no email exists */}
        {(() => {
          const emailSubscriptionVisible = invitationPage.event.additionalDetails?.find(d => d.key === 'email_subscription_visible')?.value !== 'false';
          const primaryGuest = invitationPage?.invitation.guests.find(g => !g.primaryGuestId);
          const hasEmail = primaryGuest?.email;
          
          if (emailSubscriptionVisible && !hasEmail) {
            return (
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-8 mt-8">
                <div className="max-w-2xl mx-auto">
                  <h3 className="text-xl font-semibold text-gray-800 mb-2 text-center">
                    {t('email.subscription.title')}
                  </h3>
                  <p className="text-gray-600 mb-6 text-center">
                    {t('email.subscription.description')}
                  </p>
                  
                  <form onSubmit={handleEmailSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                    <input
                      type="email"
                      name="email"
                      placeholder={t('email.modal.emailPlaceholder')}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      disabled={emailLoading}
                    />
                    <button
                      type="submit"
                      disabled={emailLoading}
                      className={`px-6 py-2 rounded-md font-medium text-white transition-all ${
                        emailLoading 
                          ? 'bg-gray-400 cursor-not-allowed' 
                          : 'bg-indigo-600 hover:bg-indigo-700 shadow-sm hover:shadow-md'
                      }`}
                    >
                      {emailLoading ? t('email.modal.submitting') : t('email.subscription.button')}
                    </button>
                  </form>
                  
                  {emailSuccess && (
                    <p className="mt-4 text-center text-sm text-green-600">
                      {t('email.subscription.success')}
                    </p>
                  )}
                  
                  {emailError && (
                    <p className="mt-4 text-center text-sm text-red-600">
                      {emailError}
                    </p>
                  )}
                  
                  <p className="mt-4 text-center text-xs text-gray-500">
                    {t('email.subscription.privacy')}
                  </p>
                </div>
              </div>
            );
          } else {
            // Show current email with option to update
            return (
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-6 mt-8">
                <div className="max-w-2xl mx-auto text-center">
                  <p className="text-sm text-gray-600 mb-2">
                    {t('email.subscription.currentEmail')}
                  </p>
                  <p className="font-medium text-gray-800 mb-3">{hasEmail}</p>
                  <button
                    onClick={() => setShowChangeEmailModal(true)}
                    className="text-sm text-indigo-600 hover:text-indigo-700 underline"
                  >
                    {t('email.subscription.changeEmail')}
                  </button>
                </div>
              </div>
            );
          }
        })()}

      </div>
      
      {/* PDF Download Modal */}
      <PDFDownloadModal
        isOpen={showPDFModal}
        onClose={() => setShowPDFModal(false)}
        onDownload={handlePDFDownload}
        eventName={invitationPage?.event.eventName}
      />

      {/* Hidden PDF version with custom dimensions - only rendered when generating PDF */}
      {pdfPageSize && design && invitationPage && (
        <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
          <InvitationPDFWrapper
            design={design}
            event={{
              id: invitationPage.event.id,
              eventName: invitationPage.event.eventName,
              eventDateTime: invitationPage.event.eventDateTime,
              createdAt: Date.now(),
              updatedAt: Date.now(),
              invitations: [],
              invitationTypes: []
            }}
            pageSize={pdfPageSize}
            removeBackground={pdfRemoveBackground}
            removeBorder={pdfRemoveBorder}
          />
        </div>
      )}
      
      {/* Email PDF Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {t('pdf.emailModal.title')}
            </h2>
            <p className="text-gray-600 mb-6">
              {t('pdf.emailModal.description')}
            </p>
            
            <form onSubmit={handleEmailPDFSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('pdf.emailModal.emailLabel')}
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder={t('email.modal.emailPlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                  disabled={emailLoading}
                  required
                />
              </div>
              
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="keepUpdated"
                  name="keepUpdated"
                  defaultChecked={true}
                  className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="keepUpdated" className="ml-2 text-sm text-gray-600">
                  {t('pdf.emailModal.keepUpdated')}
                </label>
              </div>
              
              {emailError && (
                <p className="text-sm text-red-600">
                  {emailError}
                </p>
              )}
              
              <div className="flex gap-3 justify-end mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowEmailModal(false);
                    setEmailError(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={emailLoading}
                  className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                    emailLoading 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-indigo-600 hover:bg-indigo-700'
                  }`}
                >
                  {emailLoading ? t('common.sending') : t('pdf.emailModal.sendButton')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Change Email Modal */}
      {showChangeEmailModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {t('email.changeModal.title')}
            </h2>
            <p className="text-gray-600 mb-6">
              {t('email.changeModal.description')}
            </p>
            
            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <div>
                <label htmlFor="newEmail" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('email.changeModal.emailLabel')}
                </label>
                <input
                  type="email"
                  id="newEmail"
                  name="email"
                  placeholder={t('email.modal.emailPlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                  disabled={emailLoading}
                  required
                />
              </div>
              
              {emailError && (
                <p className="text-sm text-red-600">
                  {emailError}
                </p>
              )}
              
              <div className="flex gap-3 justify-end mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowChangeEmailModal(false);
                    setEmailError(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={emailLoading}
                  className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                    emailLoading 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-indigo-600 hover:bg-indigo-700'
                  }`}
                >
                  {emailLoading ? t('common.saving') : t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventHomepage;